import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { content } from '../data/content';

const About: React.FC = () => {
  const { about } = content;

  const featureColors = [
    'text-blue-600',
    'text-purple-600',
    'text-[#f37021]', // Hermes Orange
    'text-green-600'
  ];

  const iconColors = [
    'text-blue-600 bg-blue-50 group-hover:bg-blue-600 group-hover:text-white',
    'text-purple-600 bg-purple-50 group-hover:bg-purple-600 group-hover:text-white',
    'text-[#f37021] bg-orange-50 group-hover:bg-[#f37021] group-hover:text-white',
    'text-green-600 bg-green-50 group-hover:bg-green-600 group-hover:text-white'
  ];

  return (
    <section id="about" className="min-h-screen flex items-center justify-center py-24 bg-white relative overflow-hidden">
      {/* Decorative Background Element */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gray-50/50 -skew-x-12 transform translate-x-20 z-0"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row gap-16 items-center">
          {/* Content Side - Redesigned */}
          <div className="w-full md:w-1/2 space-y-10">
            <div className="relative">
              <div className="absolute -left-4 -top-4 w-20 h-20 bg-blue-50 rounded-full blur-2xl opacity-60"></div>
              <h2 className="relative text-4xl md:text-5xl font-bold text-gray-900 tracking-tight leading-tight">
                <span className="block text-lg font-semibold text-primary-600 mb-3 tracking-widest uppercase">About Us</span>
                {about.title}
              </h2>
            </div>
            
            <div className="relative pl-6 border-l-4 border-primary-200">
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                {about.description.map((desc, index) => (
                  <p key={index} className={index === 0 ? 'font-medium text-gray-900 text-xl' : ''}>
                    {desc}
                  </p>
                ))}
              </div>
            </div>

            <div className="grid gap-4 pt-2">
              {/* @ts-ignore - features structure changed */}
              {about.features.map((feature: any, index: number) => (
                <div key={index} className="flex items-start p-5 bg-white rounded-xl border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1 group">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center shadow-sm transition-colors duration-300 mt-1 ${iconColors[index % iconColors.length]}`}>
                    <CheckCircle2 size={24} />
                  </div>
                  <div className="ml-5">
                    <h4 className={`font-bold text-xl mb-2 ${featureColors[index % featureColors.length]}`}>{feature.title}</h4>
                    <p className="text-base text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
          </div>
          
          {/* Right Side - 30 Days System Block */}
          <div className="w-full md:w-1/2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl md:aspect-[4/5] group bg-white border border-gray-100 p-8 md:p-10 flex flex-col justify-center relative">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white z-0"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl opacity-50 animate-pulse delay-700"></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 animate-gradient">30</span>
                  <span className="text-2xl md:text-3xl font-bold text-gray-800">Days</span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 leading-tight">
                  把内容做成一套<br/>
                  <span className="text-primary-600">“可复制的获客系统”</span>
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 transform transition-all duration-500 hover:scale-105 hover:shadow-md">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Before</p>
                      <p className="font-medium text-gray-700">偶尔发，不稳定</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-center -my-2 relative z-10">
                    <div className="bg-white p-1 rounded-full shadow-sm">
                      <svg className="w-6 h-6 text-primary-300 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path></svg>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-primary-50 to-white rounded-xl shadow-md border border-primary-100 transform transition-all duration-500 hover:scale-105 hover:shadow-lg">
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                    </div>
                    <div>
                      <p className="text-sm text-primary-600 font-semibold mb-1">After</p>
                      <p className="font-bold text-gray-900">稳定输出，持续获客</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-10 pt-6 border-t border-gray-100 text-center">
                  <p className="text-lg font-bold text-gray-800 bg-primary-50/50 py-2 px-4 rounded-lg inline-block">
                    🚀 持续积累信任与咨询量
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;